import { useEffect } from 'react'
import type { TestFailure } from '@/lib/types/test-cases'
import { useErrorMarkerOptional } from '../error-marker-context'
import { SectionLabel } from './section-label'

/**
 * Headline for a failure, e.g. "NameError: name 'num' is not defined".
 * Falls back gracefully when only one of errorType/message is present.
 */
export function failureHeadline(failure: TestFailure): string {
  if (failure.errorType && failure.message) {
    return `${failure.errorType}: ${failure.message}`
  }
  return failure.errorType ?? failure.message ?? 'Runtime Error'
}

/**
 * The runtime failure to display for a case, if any: the structured payload
 * when present, else one synthesized from the legacy error strings. The
 * legacy path covers results parsed from stdout stored before the structured
 * protocol shipped (and any future language that predates structured
 * failures) — synthesized failures are frameless, so they make no line claim
 * and publish no marker.
 */
export function runtimeFailureFor(result: {
  passed: boolean
  failure?: TestFailure
  error?: string
  errorType?: string
}): TestFailure | null {
  if (result.failure) {
    return result.failure.kind === 'runtime_error' ? result.failure : null
  }
  if (!result.passed && result.error) {
    return {
      kind: 'runtime_error',
      ...(result.errorType ? { errorType: result.errorType } : {}),
      message: result.error,
      userFrames: [],
    }
  }
  return null
}

interface FailurePanelProps {
  failure: TestFailure
}

/**
 * Runtime-error panel: errorType + message headline, plus the innermost user
 * frame rendered as "around line 4 in two_sum". "around" is deliberate
 * hedging (spec) — it stays honest for frameless errors, which make no line
 * claim at all (userFrames: []).
 *
 * While mounted with a frame, publishes that frame to ErrorMarkerContext so
 * Monaco shows a red gutter marker + squiggle on the line. The marker clears
 * when this panel unmounts (tab switched, results cleared) and on the user's
 * next edit (see use-monaco-editor.ts).
 */
export function FailurePanel({ failure }: FailurePanelProps) {
  const frames = failure.userFrames ?? []
  // Innermost frame is the LAST element
  const frame = frames.length > 0 ? frames[frames.length - 1] : null
  const headline = failureHeadline(failure)

  const setMarker = useErrorMarkerOptional()?.setMarker
  const frameLine = frame?.line ?? null

  useEffect(() => {
    if (!setMarker || frameLine === null) return
    setMarker({ line: frameLine, message: headline })
    return () => setMarker(null)
  }, [setMarker, frameLine, headline])

  return (
    <div className="space-y-1">
      <SectionLabel>Error</SectionLabel>
      <div className="space-y-2 rounded-lg bg-red-500/10 p-3">
        <pre className="border-0 font-mono text-sm whitespace-pre-wrap text-red-700 dark:text-red-300">
          {headline}
        </pre>
        {frame && (
          <p className="font-mono text-xs text-red-700/80 dark:text-red-300/80">
            around line {frame.line} in {frame.functionName}
          </p>
        )}
      </div>
    </div>
  )
}
